/**
 * Botão flutuante de notificações (lado direito da página).
 * Estilo semelhante ao botão de logs; mostra recebimento de fundos e permite marcar como lida / excluir.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, X, Check, Trash2 } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationsContext';
import { useWallets } from '@/contexts/WalletContext';
import { formatCurrencyDisplay } from '@/utils/currency';
import { getAddressOrWalletName } from '@/utils/formatters';
import { getBlockExplorerTxUrl } from '@/utils/blockExplorer';

function abbreviateHash(txHash) {
  if (!txHash || typeof txHash !== 'string') return '';
  const h = txHash.startsWith('0x') ? txHash : `0x${txHash}`;
  if (h.length <= 14) return h;
  return `${h.slice(0, 6)}...${h.slice(-4)}`;
}

export default function NotificationFloatingButton() {
  const { t } = useTranslation();
  const { notifications, unreadCount, markAsRead, markAllAsRead, removeNotification, removeAllNotifications } = useNotifications();
  const { wallets } = useWallets();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (open && panelRef.current && !panelRef.current.contains(e.target)) {
        const btn = document.querySelector('[data-notif-toggle]');
        if (btn && !btn.contains(e.target)) setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="fixed bottom-4 right-4 z-[9998]" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-notif-toggle
        className="w-10 h-10 rounded-full bg-purple-700 hover:bg-purple-600 text-white shadow-lg border border-purple-500/50 flex items-center justify-center transition focus:outline-none focus:ring-2 focus:ring-purple-400"
        title={t('notifications.title')}
        aria-label={t('notifications.ariaOpen')}
        aria-expanded={open}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-[10px] font-bold text-gray-900 flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed right-4 bottom-16 z-[9999] w-[min(360px,calc(100vw-2rem))] max-h-[70vh] flex flex-col rounded-lg border border-purple-500/40 bg-gray-900 shadow-2xl"
          role="dialog"
          aria-label={t('notifications.title')}
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-purple-500/20 bg-gray-800/80 rounded-t-lg shrink-0">
            <span className="text-sm font-semibold text-white">{t('notifications.title')}</span>
            <div className="flex items-center gap-1">
              {notifications.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => markAllAsRead()}
                    className="p-1.5 text-green-400 hover:bg-green-900/20 rounded transition"
                    title={t('notifications.markAllRead')}
                    aria-label={t('notifications.markAllRead')}
                  >
                    <Check size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAllNotifications()}
                    className="p-1.5 text-red-400 hover:bg-red-900/20 rounded transition"
                    title={t('notifications.deleteAll')}
                    aria-label={t('notifications.deleteAll')}
                  >
                    <Trash2 size={18} />
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 text-gray-400 hover:text-white rounded transition"
                title={t('common.close')}
                aria-label={t('common.close')}
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto min-h-0 p-2">
            {notifications.length === 0 ? (
              <p className="text-gray-500 py-6 text-center text-sm">{t('notifications.empty')}</p>
            ) : (
              <ul className="space-y-2">
                {notifications.map((n) => (
                  <li
                    key={n.id}
                    className={`rounded-lg border border-gray-700/50 bg-gray-800/50 overflow-hidden ${
                      n.read ? 'opacity-75' : ''
                    }`}
                  >
                    <div className="px-3 py-2">
                      <p className={`text-xs font-medium ${n.type === 'sent' ? 'text-sky-400' : n.type === 'incoming_funds' ? 'text-teal-400' : 'text-gray-400'}`}>
                        {n.type === 'sent' ? t('notifications.sentFunds') : n.type === 'incoming_funds' ? t('notifications.incomingFunds') : n.type}
                      </p>
                      <p className="text-sm text-white mt-0.5">
                        {n.type === 'sent'
                          ? t('notifications.sentFundsDetail', {
                              amount: formatCurrencyDisplay(n.amount ?? 0, 'USD'),
                              symbol: n.symbol || '—',
                              networkName: n.networkName || '—',
                            })
                          : t('notifications.incomingFundsDetail', {
                              amount: formatCurrencyDisplay(n.amount ?? 0, 'USD'),
                              symbol: n.symbol || '—',
                              networkName: n.networkName || '—',
                            })}
                      </p>
                      <div className="grid grid-cols-3 gap-2 mt-1 text-[10px] text-gray-500">
                        <div className="min-w-0 truncate" title={n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}>
                          {n.createdAt
                            ? new Date(n.createdAt).toLocaleString(undefined, {
                                dateStyle: 'short',
                                timeStyle: 'short',
                              })
                            : '—'}
                        </div>
                        <div className="min-w-0 truncate text-center">
                          {n.txHash ? (() => {
                            const explorerUrl = getBlockExplorerTxUrl(n.chainId, n.txHash);
                            const short = abbreviateHash(n.txHash);
                            return explorerUrl ? (
                              <a
                                href={explorerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-400 hover:text-purple-300 font-mono"
                                title={n.txHash}
                              >
                                {short}
                              </a>
                            ) : (
                              <span className="font-mono text-gray-400">{short}</span>
                            );
                          })() : '—'}
                        </div>
                        <div className="min-w-0 truncate text-right">
                          {n.type === 'incoming_funds' && n.fromAddress
                            ? (() => {
                                const payer = getAddressOrWalletName(wallets, n.fromAddress, 6, 4);
                                const name = payer.display || getAddressOrWalletName(wallets, n.fromAddress, 6, 4).display || '—';
                                return (
                                  <span className={payer.isRegistered ? 'text-white' : 'text-gray-400'} title={n.fromAddress}>
                                    {t('notifications.fromLabel', { name })}
                                  </span>
                                );
                              })()
                            : n.type === 'sent' && n.toAddress
                              ? (() => {
                                  const to = getAddressOrWalletName(wallets, n.toAddress, 6, 4);
                                  const name = to.display || '—';
                                  return (
                                    <span className={to.isRegistered ? 'text-white' : 'text-gray-400'} title={n.toAddress}>
                                      {t('notifications.toLabel', { name })}
                                    </span>
                                  );
                                })()
                              : '—'}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 px-2 py-1.5 border-t border-gray-700/50 bg-gray-800/80">
                      {!n.read && (
                        <button
                          type="button"
                          onClick={() => markAsRead(n.id)}
                          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-green-400 hover:bg-green-900/20 transition"
                        >
                          <Check size={12} />
                          {t('notifications.markRead')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeNotification(n.id)}
                        className="flex items-center gap-1 px-2 py-1 rounded text-xs text-red-400 hover:bg-red-900/20 transition ml-auto"
                      >
                        <Trash2 size={12} />
                        {t('notifications.delete')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
